import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Resolve, Router, RouterStateSnapshot } from '@angular/router';
import { Account, CartService } from '@congacommerce/ecommerce';
import { Action, Store } from '@ngrx/store';
import { from, Observable, of } from 'rxjs';
import { catchError, map, mergeMap, take, tap } from 'rxjs/operators';
import { RoutesPaths } from '../../../common/config/routes-paths';
import { D365OperationMode } from '../../../common/enums/d365/d365-operation-mode';
import {
    d365OperationModeToFlowType,
    flowTypeToMacroFlowType,
    getNumberOrNull,
} from '../../../common/functions/remap.functions';
import { eglCart2Products } from '../../../common/functions/transformation.functions';
import { DeepPartial } from '../../../common/interfaces/deep-partial';
import { ServiceError } from '../../../common/models/app/service-error';
import { LoggerService } from '../../../common/services/shared/logger.service';
import { DragonRouterService } from '../../../common/services/shared/router/dragon-router.service';
import {
    setV2CancellationRequestDate,
    setV2CartId,
    setV2Products,
} from '../../../store/actions/order-entry-v2.actions';
import { setFlowType } from '../../../store/actions/order-entry.actions';
import { setCustomerAndContact } from '../../../store/actions/user.actions';
import { FlowType, MacroFlowType } from '../../../store/models/flow-type';
import { Product } from '../../../store/models/order-entry-state_v2';
import { EglState } from '../../../store/reducers';
import { DataLoaderService } from '../services/data-loader.service';

@Injectable({ providedIn: 'root' })
export class MultiPathLoaderResolver implements Resolve<Observable<void>> {
    constructor(
        private logger: LoggerService,
        private store: Store<EglState>,
        private router: Router,
        private routerSrv: DragonRouterService,
        private cartSrv: CartService,
        private dataLoaderSrv: DataLoaderService,
    ) {}

    private FLOW_TYPE_QUERY_PARAMS_STRATEGY_MAP: FlowTypeQueryParamsStrategyMap = {
        // Mappati i query params da recuperare per ogni FlowType/MacroFlowType
        [MacroFlowType.BroadBandCessazioni]: {
            cartId: 'acart',
            assetId: 'assetId',
            assetIntegrationId: 'assetIntegrationId',
        },
        DEFAULT: null,
    };

    private FLOW_TYPE_DISPATCH_STRATEGY_MAP: FlowTypeDispatchStrategyMap = {
        // Mappati tutti i dispatch da eseguire per ogni FlowType/MacroFlowType
        [MacroFlowType.BroadBandCessazioni]: [
            { objectsToDispatch: ['cartId'], dispatchAction: (id: string) => setV2CartId({ id }) },
            {
                objectsToDispatch: ['assetId', 'assetIntegrationId'],
                startingObject: 'products',
                dispatchAction: (product: Partial<Product[]>) => setV2Products({ products: product }),
            },
            {
                objectsToDispatch: [],
                dispatchAction: () =>
                    setV2CancellationRequestDate({
                        cancellationRequestDate: new Date(),
                    }),
            },
        ],
        DEFAULT: null,
    };

    resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<void> {
        // load-multi-path/operation/:operationMode?acart=:cartId&assetId=:assetId&assetIntegrationId=:assetIntegrationId

        // Parto dalla rotta
        console.log('Multi path loader: ', route.params, route.queryParams);
        return of(route)
            .pipe(
                // Recupero i pathParams e i queryParams
                map(({ params, queryParams }) => ({
                    operationMode: getNumberOrNull(params?.operationMode) as D365OperationMode,
                    queryParams,
                })),
                // Verifico la presenza dell'operationMode
                tap(({ operationMode }) => {
                    if (!operationMode || !Object.values(D365OperationMode).includes(operationMode)) {
                        throw new Error(`Invalid operationMode: ${route.params?.operationMode}`);
                    }
                }),
                // Mappo FlowType e MacroFlowType a partire dall'operationMode di D365
                map(({ operationMode, ...data }) => ({
                    ...data,
                    operationMode,
                    flowType: d365OperationModeToFlowType(operationMode),
                    macroFlowType: flowTypeToMacroFlowType(d365OperationModeToFlowType(operationMode)),
                })),
                // Verifico che l'operationMode abbia corrispondenza come FlowType e MacroFlowType
                tap(({ operationMode, flowType, macroFlowType }) => {
                    if (!flowType) {
                        throw new Error(`Missing FlowType for the given D365 OperationMode: ${operationMode}`);
                    }
                    if (!macroFlowType) {
                        throw new Error(`Missing MacroFlowType for the given FlowType: ${flowType}`);
                    }
                }),
                // Mappo i query params obbligatori richiesto da ogni FlowType o MacroFlowType
                map(({ flowType, macroFlowType, queryParams, ...data }) => ({
                    ...data,
                    flowType,
                    macroFlowType,
                    queryParams,
                    queryParamsValues: Object.entries(
                        this.FLOW_TYPE_QUERY_PARAMS_STRATEGY_MAP[flowType] ||
                            this.FLOW_TYPE_QUERY_PARAMS_STRATEGY_MAP[macroFlowType] ||
                            this.FLOW_TYPE_QUERY_PARAMS_STRATEGY_MAP.DEFAULT,
                    ).reduce((aggregatorMap, [key, values]) => {
                        // Mi creo una mappa <chiave, valore> dove la chiave è il nome del campo del query params obbligatorio e il valore è quello che ci arriva nel rispettivo query params
                        return aggregatorMap.set(key, queryParams[values]);
                    }, new Map<string, string>()),
                })),
                // Verifico la presenza dei query params obbligatori
                tap(({ queryParamsValues, flowType }) => {
                    // Array delle chiavi appena mappate
                    const keysData = Array.from(queryParamsValues.keys());
                    // Mi recupero l'array di tutti i valori e, nel caso un valore mancasse, lo mappo nell'array dei dati mancanti
                    const missingData = Array.from(queryParamsValues.values())
                        .map((arr, idx) => (!arr ? keysData[idx] : null))
                        // Filtro l'array tenendo solo i campi "pieni"
                        .filter(Boolean);
                    // Se c'è almeno un dato mancante, lancio l'errore con il primo dato mancante
                    if (missingData?.length > 0) {
                        throw new Error(`Missing ${missingData[0]} for the given flowType: ${flowType}`);
                    }
                }),
            )
            .pipe(
                mergeMap(({ ...data }) =>
                    route?.queryParams?.assetId
                        ? this.dataLoaderSrv.getAssets([route?.queryParams?.assetId]).pipe(
                              map(({ products, accountNumber }) => ({
                                  ...data,
                                  products,
                                  accountNumber,
                              })),
                          )
                        : route?.queryParams?.acart
                          ? this.cartSrv
                                .getCartWithId(route?.queryParams?.acart)
                                .pipe(
                                    map((cart) => ({
                                        ...data,
                                        products: eglCart2Products(cart),
                                        accountNumber: (cart?.Account as ExtendedAccount)?.AccountNumber,
                                    })),
                                )
                                .pipe(take(1))
                          : of({ ...data, products: [], accountNumber: null }),
                ),
                map(({ products, ...data }) => ({
                    ...data,
                    products: products.map((prod, idx) => ({ ...prod, idx: idx }) as DeepPartial<Product>),
                })),
                mergeMap(({ accountNumber, ...data }) =>
                    accountNumber
                        ? this.dataLoaderSrv.getAccountContact(accountNumber).pipe(
                              map((account) => ({
                                  ...data,
                                  account,
                              })),
                              tap(({ account }) => this.store.dispatch(setCustomerAndContact({ customer: account }))),
                          )
                        : of({ ...data }),
                ),
                // Mappo le action da dispatchare per ogni FlowType/MacroFlowType
                map(({ queryParamsValues, flowType, macroFlowType, ...data }) => ({
                    ...data,
                    queryParamsValues,
                    flowType,
                    macroFlowType,
                    toDispatch: (
                        this.FLOW_TYPE_DISPATCH_STRATEGY_MAP[flowType] ||
                        this.FLOW_TYPE_DISPATCH_STRATEGY_MAP[macroFlowType] ||
                        this.FLOW_TYPE_DISPATCH_STRATEGY_MAP.DEFAULT
                    ).map(({ objectsToDispatch, dispatchAction, startingObject }) => {
                        let params = null;
                        // Se i campi da dispatchare sono più di 1, mi creo un oggetto da dispachare in base ai campi richiesti
                        if (objectsToDispatch.length > 1) {
                            params = objectsToDispatch.reduce((aggregatorMap, value) => {
                                // Per ogni oggetto scrivo il nome e il suo valore (es: assetId: aaaaaaa)
                                return Array.isArray(aggregatorMap)
                                    ? aggregatorMap.map((prod) => {
                                          return { ...prod, ...{ [value]: queryParamsValues.get(value) } };
                                      })
                                    : { ...aggregatorMap, ...{ [value]: queryParamsValues.get(value) } };
                            }, data[startingObject] || {});
                            // Se il campo da dispatchare è uno solo, ne salvo il valore
                        } else if (objectsToDispatch.length > 0) {
                            params = queryParamsValues.get(objectsToDispatch[0]);
                        }
                        return dispatchAction(params);
                    }),
                })),
            )
            .pipe(
                // Dispatch dei dati calcolati nel map precedente
                tap(({ toDispatch }) => toDispatch.forEach((action) => this.store.dispatch(action))),
                // Dispatch del flowType
                tap(({ flowType }) => this.store.dispatch(setFlowType({ flowType }))),
                // Indirizzo l'utente verso il prossimo elemento del routing
                map(() =>
                    this.routerSrv.next({
                        acart: route?.queryParams?.acart,
                    }),
                ),
                // In caso di KO, indirizzo l'utente verso la pagina di KO
                catchError((error: ServiceError) => {
                    this.logger.error('Attenzione', error?.message, error, true);
                    if (error?.level === 'LOW') {
                        return from(
                            this.router.navigate([RoutesPaths.KoCreditCheck], {
                                queryParams: { errorCode: 'GENERIC', message: error?.message },
                            }),
                        );
                    }
                    return from(this.router.navigate([RoutesPaths.Error500]));
                }),
            ) as unknown as Observable<void>;
    }
}

/**
 * @description Campi da dispatchare
 * @param objectsToDispatch array con la lista dei nomi di campi da dispatchare presi dai queryParams. Se dobbiamo dispatchare un valore fisso, lasciare l'array vuoto
 * @param dispatchAction: funzione di dispatch con relativo input. Se non serve l'input, settare direttamente il valore da dispatchare e lasciare vuota la parentesi dei campi in input
 */
type DispatchActionEnricher = {
    objectsToDispatch: string[];
    dispatchAction: (...params: any) => Action;
    startingObject?: any;
};

type FlowTypeQueryParamsStrategyMap = {
    [key in FlowType | MacroFlowType]?: {
        [key in string]: string;
    };
} & {
    DEFAULT: null;
};

type FlowTypeDispatchStrategyMap = {
    [key in FlowType | MacroFlowType]?: DispatchActionEnricher[];
} & {
    DEFAULT: null;
};

class ExtendedAccount extends Account {
    AccountNumber: string;
}
